import { useState, useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import { Field, inputCls, selectCls, textareaCls } from "./form-controls";

// What the schedule UI knows about a task it's editing — a subset of
// MaintenanceSchedule's ScheduleTask shape.
export interface SchedulableTask {
  _id: string;
  title: string;
  description: string | null;
  scheduledFor: string | null;
  recurringIntervalDays: number | null;
  contractor: { _id: string } | null;
  unit: { _id: string } | null;
}

interface Contractor {
  _id: string;
  name: string;
  serviceType: string;
}

interface Unit {
  _id: string;
  label: string;
}

// Landlord-facing form for creating a new maintenance task or editing
// an existing one (e.g. scheduling a tenant-submitted request). Pass
// `existingTask` to switch the form into edit mode. Pass
// `initialScheduledFor` to pre-fill the date for a brand-new task
// (e.g. when the user click-selected an empty calendar slot).
export function ScheduleMaintenanceDialog({
  open,
  onClose,
  propertySlug,
  existingTask,
  initialScheduledFor,
}: {
  open: boolean;
  onClose: () => void;
  propertySlug: string;
  existingTask?: SchedulableTask;
  initialScheduledFor?: string; // ISO datetime string
}) {
  const contractors = useQuery(
    anyApi.contractors.list,
    open ? {} : "skip",
  ) as Contractor[] | undefined;
  const units = useQuery(
    anyApi.units.listForProperty,
    open ? { propertySlug } : "skip",
  ) as Unit[] | undefined;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [recurring, setRecurring] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset / pre-fill when the dialog opens or the task being edited
  // changes. Reading from existingTask?._id in deps avoids bouncing on
  // every parent re-render that hands a new object reference.
  useEffect(() => {
    if (!open) return;
    setTitle(existingTask?.title ?? "");
    setDescription(existingTask?.description ?? "");
    setScheduledFor(
      existingTask?.scheduledFor
        ? formatLocalForInput(existingTask.scheduledFor)
        : initialScheduledFor
          ? formatLocalForInput(initialScheduledFor)
          : "",
    );
    setContractorId(existingTask?.contractor?._id ?? "");
    setUnitId(existingTask?.unit?._id ?? "");
    setRecurring(
      existingTask?.recurringIntervalDays
        ? String(existingTask.recurringIntervalDays)
        : "",
    );
    setError(null);
  }, [open, existingTask?._id, initialScheduledFor]);

  const submit = useMutation(anyApi.maintenance.schedule);
  const cancelTask = useMutation(anyApi.maintenance.cancel);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submit({
        taskId: existingTask?._id,
        propertySlug,
        title,
        description: description || undefined,
        scheduledFor: scheduledFor
          ? new Date(scheduledFor).toISOString()
          : undefined,
        contractorId: contractorId || undefined,
        unitId: unitId || undefined,
        recurringIntervalDays: recurring ? Number(recurring) : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTask = async () => {
    if (!existingTask) return;
    setSubmitting(true);
    setError(null);
    try {
      await cancelTask({ taskId: existingTask._id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={existingTask ? "Edit maintenance task" : "Schedule maintenance"}
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <Field label="Title" required>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            placeholder="e.g. Annual furnace inspection"
            className={inputCls}
          />
        </Field>
        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Notes for the visit"
            className={`${textareaCls} resize-none`}
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="When">
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Repeats">
            <select
              value={recurring}
              onChange={(e) => setRecurring(e.target.value)}
              className={selectCls}
            >
              <option value="">Doesn't repeat</option>
              <option value="30">Monthly</option>
              <option value="90">Quarterly</option>
              <option value="180">Every 6 months</option>
              <option value="365">Yearly</option>
            </select>
          </Field>
        </div>
        <Field label="Contractor">
          <select
            value={contractorId}
            onChange={(e) => setContractorId(e.target.value)}
            className={selectCls}
          >
            <option value="">(none)</option>
            {(contractors ?? []).map((c) => (
              <option key={c._id} value={c._id}>
                {c.name} · {c.serviceType}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Scope">
          <select
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            className={selectCls}
          >
            <option value="">Property-wide</option>
            {(units ?? []).map((u) => (
              <option key={u._id} value={u._id}>
                {u.label}
              </option>
            ))}
          </select>
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex items-center justify-end gap-3 pt-2">
          {existingTask && (
            <button
              type="button"
              onClick={handleCancelTask}
              disabled={submitting}
              className="mr-auto text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 transition-colors"
            >
              Cancel this task
            </button>
          )}
          <button
            type="submit"
            disabled={submitting || !title.trim()}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {submitting
              ? "Saving..."
              : existingTask
                ? "Save changes"
                : "Create task"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// Convert ISO string to "YYYY-MM-DDTHH:mm" suitable for the
// browser's datetime-local input control.
function formatLocalForInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
